import { API_BASE_URL } from '../config/api-config';

/**
 * API 공통 fetcher
 * @param {string} endpoint - API 엔드포인트
 * @param {object} options - fetch 옵션
 * @returns {Promise} API 응답
 */
const fetcher = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(url, config);

    // Content-Type 확인
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    // 응답 파싱
    const data = isJson ? await response.json() : await response.text();

    // 에러 처리
    if (!response.ok) {
      const error = new Error(data.message || '요청 실패');
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('API 요청 실패:', error);
    throw error;
  }
};

const isAuthEndpoint = (endpoint) => {
  return endpoint.includes('/api/user/login')
    || endpoint.includes('/api/user/refresh')
    || endpoint.includes('/api/user/register')
    || endpoint.includes('/api/user/check-duplicate');
};

const getCurrentAccessToken = async () => {
  try {
    const { useAuthStore } = await import('../../stores/auth-store');
    return useAuthStore.getState().token;
  } catch {
    return localStorage.getItem('token');
  }
};

const tryRefreshAccessToken = async () => {
  try {
    const { useAuthStore } = await import('../../stores/auth-store');
    return await useAuthStore.getState().refreshAccessToken();
  } catch {
    return false;
  }
};

const requestWithAutoRefresh = async (endpoint, options = {}, token = null, requestOptions = {}) => {
  const currentToken = token || await getCurrentAccessToken();
  const shouldAttachAuth = !!currentToken && !isAuthEndpoint(endpoint);

  const firstOptions = {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(shouldAttachAuth ? { Authorization: `Bearer ${currentToken}` } : {}),
    },
  };

  try {
    return await fetcher(endpoint, firstOptions);
  } catch (error) {
    const shouldTryRefresh =
      error?.status === 401
      && !requestOptions.skipAuthRefresh
      && !isAuthEndpoint(endpoint);

    if (!shouldTryRefresh) {
      throw error;
    }

    const refreshed = await tryRefreshAccessToken();
    if (!refreshed) {
      throw error;
    }

    const retryToken = await getCurrentAccessToken();
    const retryOptions = {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(retryToken ? { Authorization: `Bearer ${retryToken}` } : {}),
      },
    };

    return fetcher(endpoint, retryOptions);
  }
};

/**
 * GET 요청
 * @param {string} endpoint - API 엔드포인트
 * @param {object} params - 쿼리 파라미터
 * @param {string} token - JWT 토큰 (선택)
 * @returns {Promise} API 응답
 */
export const get = async (endpoint, params = {}, token = null, requestOptions = {}) => {
  const queryString = new URLSearchParams(
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {})
  ).toString();

  const url = queryString ? `${endpoint}?${queryString}` : endpoint;

  const options = {
    method: 'GET',
  };

  return requestWithAutoRefresh(url, options, token, requestOptions);
};

/**
 * POST 요청
 * @param {string} endpoint - API 엔드포인트
 * @param {object} data - 요청 데이터
 * @param {string} token - JWT 토큰 (선택)
 * @returns {Promise} API 응답
 */
export const post = async (endpoint, data = {}, token = null, requestOptions = {}) => {
  const options = {
    method: 'POST',
    body: JSON.stringify(data),
  };

  return requestWithAutoRefresh(endpoint, options, token, requestOptions);
};

/**
 * PUT 요청
 * @param {string} endpoint - API 엔드포인트
 * @param {object} data - 요청 데이터
 * @param {string} token - JWT 토큰 (선택)
 * @returns {Promise} API 응답
 */
export const put = async (endpoint, data = {}, token = null, requestOptions = {}) => {
  const options = {
    method: 'PUT',
    body: JSON.stringify(data),
  };

  return requestWithAutoRefresh(endpoint, options, token, requestOptions);
};

/**
 * PATCH 요청
 * @param {string} endpoint - API 엔드포인트
 * @param {object} data - 요청 데이터
 * @param {string} token - JWT 토큰 (선택)
 * @returns {Promise} API 응답
 */
export const patch = async (endpoint, data = {}, token = null, requestOptions = {}) => {
  const options = {
    method: 'PATCH',
    body: JSON.stringify(data),
  };

  return requestWithAutoRefresh(endpoint, options, token, requestOptions);
};

/**
 * DELETE 요청
 * @param {string} endpoint - API 엔드포인트
 * @param {object} data - 요청 데이터 (선택)
 * @param {string} token - JWT 토큰 (선택)
 * @returns {Promise} API 응답
 */
export const del = async (endpoint, data = {}, token = null, requestOptions = {}) => {
  const options = {
    method: 'DELETE',
  };

  if (Object.keys(data).length > 0) {
    options.body = JSON.stringify(data);
  }

  return requestWithAutoRefresh(endpoint, options, token, requestOptions);
};

export default fetcher;
